import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { Chip, Stack, Popover, Box } from "@mui/material"
import FieldsGetter from "components/fields/fields";
import { tableFilterVariables } from "constants/forms/filter";
import { format } from "date-fns";
import { getLastNYears } from "utils/common/getLast10Years";
import { getLast12Months } from "utils/common/getLast12Months";

export const fixedPeriods = [
    {
        label: 'Últimos 30 dias',
        value: 'last_30_days',
    },
    {
        label: 'Últimos 365 dias',
        value: 'last_365_days',
    },
    ...getLastNYears(8),
]

const GAP = 8 // equivale ao spacing={1} do MUI (1 * 8px)

export default function ReportYearFilter({
    reporters,
    setReporters,
    users,
    local,
    setLocal,
    selected,
    setPeriod,
    monthly,
    customDate,
    showSelect,
    fullYear,
    selectedYear,
    disabled

}) {
    const [formData, setFormData] = useState({})
    const [visibleCount, setVisibleCount] = useState(null) // null = ainda não mediu (renderiza tudo escondido)
    const [anchorEl, setAnchorEl] = useState(null)

    const containerRef = useRef(null)
    const measureRefs = useRef([])
    const moreButtonRef = useRef(null)

    const periods = [
        ...(monthly
            ? getLast12Months(fullYear, fullYear ? 12 : 6).map((datas) => ({
                label: fullYear ? datas.month : `${datas.month} - ${datas.year}`,
                value: `${datas.month}-${selectedYear ? selectedYear : datas.year}`,
            }))
            : fixedPeriods),
    ];

    useEffect(() => {
        if (formData?.data) setPeriod(`startDate=${format(new Date(formData?.data?.startDate), 'yyyy-MM-dd')}&endDate=${format(new Date(formData?.data?.endDate), 'yyyy-MM-dd')}`)
    }, [formData])

    // Recalcula quantos chips cabem numa linha
    const recalculate = useCallback(() => {
        const container = containerRef.current
        if (!container || measureRefs.current.length === 0) return

        const containerWidth = container.offsetWidth
        const moreWidth = moreButtonRef.current ? moreButtonRef.current.offsetWidth + GAP : 0

        let usedWidth = 0
        let count = 0

        for (let i = 0; i < periods.length; i++) {
            const el = measureRefs.current[i]
            if (!el) continue
            const chipWidth = el.offsetWidth + (i > 0 ? GAP : 0)

            // largura restante precisa caber o chip atual + (se não for o último) o botão "mais"
            const isLast = i === periods.length - 1
            const reserve = isLast ? 0 : moreWidth

            if (usedWidth + chipWidth + reserve <= containerWidth) {
                usedWidth += chipWidth
                count++
            } else {
                break
            }
        }

        // Se coube tudo, não precisa do botão "mais"
        if (count === periods.length) {
            setVisibleCount(periods.length)
        } else {
            setVisibleCount(Math.max(count, 1))
        }
    }, [periods.length])

    useLayoutEffect(() => {
        measureRefs.current = measureRefs.current.slice(0, periods.length)
        recalculate()
    }, [periods.length, monthly, fullYear, selectedYear, recalculate])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const resizeObserver = new ResizeObserver(() => recalculate())
        resizeObserver.observe(container)

        return () => resizeObserver.disconnect()
    }, [recalculate])

    const handleOpenMore = (event) => setAnchorEl(event.currentTarget)
    const handleCloseMore = () => setAnchorEl(null)

    const visiblePeriods = visibleCount === null ? [] : periods.slice(0, visibleCount)
    const hiddenPeriods = visibleCount === null ? [] : periods.slice(visibleCount)

    const renderChip = (chip, extraStyle = {}) => (
        <Chip
            key={chip.value}
            size="medium"
            disabled={disabled}
            style={{
                fontSize: "15px",
                fontWeight: "500",
                borderRadius: "8px",
                ...(disabled ? { cursor: 'not-allowed' } : { cursor: 'pointer' }),
                ...(chip.value === selected ? { background: "#72DDF7" } : { opacity: 0.7 }),
                ...extraStyle,
            }}
            label={chip.label}
            onClick={() => {
                if (setPeriod) setPeriod(chip.value)
                handleCloseMore()
            }}
        />
    )

    return (
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "1px" }} >
            <div style={{ margin: "0 0 10px 20px", flex: 1, minWidth: 0 }} >
                <h6>{customDate ? 'Selecione um intervalo de datas' : showSelect ? 'Períodos' : `Meses`}</h6>
                {customDate ? <>
                    <FieldsGetter props={{ ...tableFilterVariables({ owners: [] }).find(x => x.fieldId === 'data'), formData, setFormData, width: "400px", disabled }} />
                </> : <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
                    {/* Linha real, sem quebra */}
                    <Stack
                        sx={{ flexWrap: 'nowrap', overflow: 'hidden', gap: 0.1 }}
                        direction="row"
                        spacing={1}
                    >
                        {visiblePeriods.map((chip) => renderChip(chip))}

                        {hiddenPeriods.length > 0 && (
                            <Chip
                                ref={moreButtonRef}
                                size="medium"
                                disabled={disabled}
                                onClick={handleOpenMore}
                                label={`Outros`}
                                style={{
                                    fontSize: "15px",
                                    fontWeight: "500",
                                    borderRadius: "8px",
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: 0.9,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                    </Stack>

                    {/* Linha fantasma p/ medir a largura real de cada chip (invisível, não quebra) */}
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            visibility: 'hidden',
                            flexWrap: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: -1,
                        }}
                    >
                        {periods.map((chip, i) => (
                            <Chip
                                key={chip.value}
                                ref={(el) => { measureRefs.current[i] = el }}
                                size="medium"
                                label={chip.label}
                                style={{ fontSize: "15px", fontWeight: "500", borderRadius: "8px" }}
                            />
                        ))}
                        <Chip
                            size="medium"
                            label="+00 mais"
                            style={{ fontSize: "15px", fontWeight: "500", borderRadius: "8px" }}
                        />
                    </Stack>

                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleCloseMore}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <Box sx={{ p: 1.5, maxWidth: 320, display: 'flex', flexWrap: 'wrap', gap: 1, backgroundColor: '#f0f9ff' }}>
                            {hiddenPeriods.map((chip) => renderChip(chip))}
                        </Box>
                    </Popover>
                </div>}
            </div>

            {showSelect && <div style={{ display: "flex" }} >
                <div style={{ margin: "0 10px 10px 0", width: "100%" }} >
                    <h6>Colaborador</h6>
                    <FieldsGetter props={{ fieldId: 'reporters', width: "200px", options: users, type: 'multiSelect', formData: reporters, setFormData: setReporters, disabled }} />
                </div><div style={{ margin: "0 20px 10px 0" }} >
                    <h6>Site</h6>
                    <FieldsGetter props={{ ...tableFilterVariables({ owners: [] }).find(x => x.fieldId === 'local'), value: local, onChange: (_, localId) => setLocal(localId), disabled }} />
                </div>
            </div>}
        </div>
    )
}